import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardBody,
  Heading,
  Input,
  Stack,
  SimpleGrid,
  useTheme,
  Skeleton,
  Text,
  Flex,
  Select,
} from "@chakra-ui/react";
import getAxios from "../../hooks/useAxios";
import { Theme } from "../../theme";
import Cookies from "js-cookie";

type RideSummary = {
  amount: number;
  rides: number;
};

type DailySummary = RideSummary & {
  date: string; // format: dd-mm-yyyy
};

export type StatsType = {
  daily: DailySummary;
  weekly: RideSummary;
  monthly: RideSummary;
  total: RideSummary;
};

type ParcelSummary = {
  amount: number;
  parcels: number;
};

type ParcelDailySummary = ParcelSummary & {
  date: string;
};

type ParcelStatsType = {
  daily: ParcelDailySummary;
  weekly: ParcelSummary;
  monthly: ParcelSummary;
  total: ParcelSummary;
};

const RevenueDashboard = () => {
  const theme = useTheme<Theme>();
  const axios = getAxios();
  const access_token = Cookies.get("x-admin-auth");

  const [stats, setStats] = React.useState<StatsType>();
  const [filteredStats, setFilteredStats] = React.useState<RideSummary>();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isTodaysDataFetching, setIsTodaysDataFetching] = React.useState(true);

  const [totalDrivers, setTotalDrivers] = React.useState<number>();
  const [isDriverDataFetching, setIsDriverDataFetching] = React.useState(true);
  const [totalUsers, setTotalUsers] = React.useState<number>();
  const [isUserDataFetching, setIsUserDataFetching] = React.useState(true);

  const [locality, setLocality] = React.useState("");
  const [isDataFetchingArea, setIsDataFetchingArea] = React.useState(true);
  const [area, setArea] = React.useState<any>([]);

  const [parcelLocality, setParcelLocality] = React.useState("");
  const [parcelStats, setParcelStats] = React.useState<ParcelStatsType>();
  const [isParcelDataFetching, setIsParcelDataFetching] = React.useState(true);
  const [parcelStartDate, setParcelStartDate] = useState("");
  const [parcelEndDate, setParcelEndDate] = useState("");

  const [filteredParcelStats, setFilteredParcelStats] =
    React.useState<ParcelSummary>();

  const handleParcelFilterByArea = (val: string) => {
    setParcelLocality(val);
  };

  const buildDriverUrlForStats = () => {
    let url = `incomes/summary`;

    if (locality && locality !== "all") {
      url += `?driving_area=${locality}`;
    }

    return url;
  };

  const getTodaysStats = async (url: string) => {
    setIsTodaysDataFetching(true);
    try {
      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });
      console.log("all stats", res.data);
      setStats(res.data);
    } catch (error) {
      console.log("error fetching stats", error);
    } finally {
      setIsTodaysDataFetching(false);
    }
  };

  const getFilteredStats = async (start: any, end: any) => {
    setIsTodaysDataFetching(true);

    try {
      const res = await axios.get(
        `/incomes/summary?start_date=${formatToDDMMYYYY(start)}&end_date=${formatToDDMMYYYY(end)}`,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      );
      console.log("filtered stats", res.data);
      setFilteredStats(res.data.total);
    } catch (error) {
      console.log("error fetching stats", error);
      return undefined;
    } finally {
      setIsTodaysDataFetching(false);
    }
  };

  const formatToDDMMYYYY = (dateString: any) => {
    if (!dateString) return "";

    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const getAllDrivers = async (url: string) => {
    setIsDriverDataFetching(true);
    try {
      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      setTotalDrivers(res.data.pagination.total);
    } catch (error) {
      console.log("error listing drivers", error);
    } finally {
      setIsDriverDataFetching(false);
    }
  };

  const getAllUsers = async (url: string) => {
    setIsUserDataFetching(true);
    try {
      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      setTotalUsers(res.data.pagination.total);
    } catch (error) {
      console.log("error listing users", error);
    } finally {
      setIsUserDataFetching(false);
    }
  };

  const handleFilterByArea = (val: string) => {
    setLocality(val);
  };

  const getArea = async () => {
    setIsDataFetchingArea(true);
    try {
      const res = await axios.get(`/driving-areas`, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });
      console.log("all admin", res.data);
      setArea(res.data?.data);
    } catch (error) {
      console.log("error listing ad", error);
    } finally {
      setIsDataFetchingArea(false);
    }
  };

  const getParcelStats = async () => {
    setIsParcelDataFetching(true);

    try {
      let url = `/parcels/incomes/summary`;

      if (parcelLocality && parcelLocality !== "all") {
        url += `?driving_area=${parcelLocality}`;
      }

      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      console.log("parcel stats", res.data);
      setParcelStats(res.data);
    } catch (error) {
      console.log("error fetching parcel stats", error);
    } finally {
      setIsParcelDataFetching(false);
    }
  };

  const getFilteredParcelStats = async (start: string, end: string) => {
    setIsParcelDataFetching(true);

    try {
      const res = await axios.get(
        `/parcels/incomes/summary?start_date=${formatToDDMMYYYY(
          start,
        )}&end_date=${formatToDDMMYYYY(end)}`,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      );

      console.log("filtered parcel stats", res.data);

      setFilteredParcelStats(res.data?.total);
    } catch (error) {
      console.log("error fetching filtered parcel stats", error);
    } finally {
      setIsParcelDataFetching(false);
    }
  };

  React.useEffect(() => {
    getParcelStats();
  }, [parcelLocality]);

  React.useEffect(() => {
    if (!parcelStartDate || !parcelEndDate) return;

    getFilteredParcelStats(parcelStartDate, parcelEndDate);
  }, [parcelStartDate, parcelEndDate]);

  React.useEffect(() => {
    getArea();
  }, []);

  React.useEffect(() => {
    getAllDrivers(`/drivers?status=approved&blocked=false`);
  }, []);

  React.useEffect(() => {
    getAllUsers(`/riders`);
  }, []);
  // Dummy calculation (replace with real API/filter logic)

  React.useEffect(() => {
    getTodaysStats(buildDriverUrlForStats());
  }, [locality]);

  React.useEffect(() => {
    if (!startDate || !endDate) return;
    getFilteredStats(startDate, endDate);
  }, [startDate, endDate]);

  const CountCard = ({ title, total, loading }: any) => (
    <Card shadow="md" borderRadius="xl">
      <CardBody>
        <Stat>
          <StatLabel fontSize="md" fontWeight="bold">
            {title}
          </StatLabel>
          <Skeleton isLoaded={!loading}>
            <StatNumber fontSize="2xl">{total} </StatNumber>
          </Skeleton>
        </Stat>
      </CardBody>
    </Card>
  );

  return (
    <Box p={6}>
      <Text
        sx={{
          fontSize: theme.fontSizes.subHeading1,
          margin: "0 0 2rem 0",
        }}
      >
        User & Driver Statistics
      </Text>

      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 4 }}
        spacing={6}
        marginBottom={"2rem"}
      >
        <CountCard
          title="Total Drivers"
          total={totalDrivers}
          loading={isDriverDataFetching}
        />
        <CountCard
          title="Total Users"
          total={totalUsers}
          loading={isUserDataFetching}
        />
      </SimpleGrid>

      <Flex alignItems={"center"} margin={"0 0 2rem 0"} gap={"2rem"}>
        <Text
          sx={{
            fontSize: theme.fontSizes.subHeading1,
            width: "max-content",
          }}
        >
          Ride & Earnings Summary
        </Text>
        <Box sx={{ height: "100%" }}>
          <Select
            style={{ height: "3.3rem", width: "10rem" }}
            placeholder={
              isDataFetchingArea ? "Loading Areas..." : "Select Area"
            }
            isDisabled={isDataFetchingArea}
            value={locality}
            onChange={(e) => handleFilterByArea(e.target.value)}
          >
            <option value="all">All</option>
            {area.map((i: any, n: number) => {
              return (
                <option key={n} value={i.name}>
                  {i.name}
                </option>
              );
            })}
          </Select>
        </Box>
      </Flex>

      {/* Top 4 Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        <StatCard
          title="Today"
          rides={stats?.daily?.rides}
          amount={stats?.daily?.amount}
          loading={isTodaysDataFetching}
        />
        <StatCard
          title="Last 7 Days"
          rides={stats?.weekly?.rides}
          amount={stats?.weekly?.amount}
          loading={isTodaysDataFetching}
        />
        <StatCard
          title="Last 30 Days"
          rides={stats?.monthly?.rides}
          amount={stats?.monthly?.amount}
          loading={isTodaysDataFetching}
        />
        <StatCard
          title="Lifetime Stats"
          rides={stats?.total?.rides}
          amount={stats?.total?.amount}
          loading={isTodaysDataFetching}
        />
      </SimpleGrid>

      {/* Date Filter Section */}
      <Box mt={10}>
        <Heading size="md" mb={4}>
          Ride & Earnings Filter by Date Range
        </Heading>

        <Stack direction={{ base: "column", md: "row" }} spacing={4} mb={6}>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Stack>

        {/* Filter Result Card */}
        {filteredStats && (
          <Card shadow="lg" borderRadius="xl">
            <CardBody>
              <Stat>
                <StatLabel fontSize="lg" fontWeight="bold">
                  Selected Range Summary
                </StatLabel>
                <StatNumber fontSize="2xl">
                  {filteredStats?.rides} Rides
                </StatNumber>
                <StatHelpText fontSize="xl">
                  ₹{filteredStats?.amount?.toFixed(2)}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        )}
      </Box>

      {/* Parcel Earnings Summary */}
      <Box mt={10}>
        <Flex alignItems={"center"} margin={"0 0 2rem 0"} gap={"2rem"}>
          <Text
            sx={{
              fontSize: theme.fontSizes.subHeading1,
              width: "max-content",
            }}
          >
            Parcel Earnings Summary
          </Text>

          <Box sx={{ height: "100%" }}>
            <Select
              style={{ height: "3.3rem", width: "10rem" }}
              placeholder={
                isDataFetchingArea ? "Loading Areas..." : "Select Area"
              }
              isDisabled={isDataFetchingArea}
              value={parcelLocality}
              onChange={(e) => handleParcelFilterByArea(e.target.value)}
            >
              <option value="all">All</option>

              {area.map((i: any, n: number) => {
                return (
                  <option key={n} value={i.name}>
                    {i.name}
                  </option>
                );
              })}
            </Select>
          </Box>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <ParcelStatCard
            title="Today"
            parcels={parcelStats?.daily?.parcels}
            amount={parcelStats?.daily?.amount}
            loading={isParcelDataFetching}
          />

          <ParcelStatCard
            title="Last 7 Days"
            parcels={parcelStats?.weekly?.parcels}
            amount={parcelStats?.weekly?.amount}
            loading={isParcelDataFetching}
          />

          <ParcelStatCard
            title="Last 30 Days"
            parcels={parcelStats?.monthly?.parcels}
            amount={parcelStats?.monthly?.amount}
            loading={isParcelDataFetching}
          />

          <ParcelStatCard
            title="Lifetime Stats"
            parcels={parcelStats?.total?.parcels}
            amount={parcelStats?.total?.amount}
            loading={isParcelDataFetching}
          />
        </SimpleGrid>
      </Box>

      {/* Parcel Date Filter Section */}
      <Box mt={10}>
        <Heading size="md" mb={4}>
          Parcel Earnings Filter by Date Range
        </Heading>

        <Stack direction={{ base: "column", md: "row" }} spacing={4} mb={6}>
          <Input
            type="date"
            value={parcelStartDate}
            onChange={(e) => setParcelStartDate(e.target.value)}
          />

          <Input
            type="date"
            value={parcelEndDate}
            onChange={(e) => setParcelEndDate(e.target.value)}
          />
        </Stack>

        {/* Filter Result Card */}
        {filteredParcelStats && (
          <Card shadow="lg" borderRadius="xl">
            <CardBody>
              <Stat>
                <StatLabel fontSize="lg" fontWeight="bold">
                  Selected Parcel Range Summary
                </StatLabel>

                <StatNumber fontSize="2xl">
                  {filteredParcelStats?.parcels} Parcels
                </StatNumber>

                <StatHelpText fontSize="xl">
                  ₹{filteredParcelStats?.amount?.toFixed(2)}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default RevenueDashboard;

export const StatCard = ({ title, rides, amount, loading }: any) => {
  const theme = useTheme<Theme>();

  return (
    <Card shadow="md" borderRadius="xl">
      <CardBody>
        <Stat>
          <StatLabel fontSize="md" fontWeight="bold">
            {title}
          </StatLabel>
          <Skeleton isLoaded={!loading}>
            <StatNumber fontSize="2xl">{rides} Rides</StatNumber>
          </Skeleton>
          <Skeleton isLoaded={!loading}>
            <StatHelpText fontSize="xl">₹{amount?.toFixed(2)}</StatHelpText>
          </Skeleton>
          {/* <Flex sx={{ justifyContent: "flex-start", alignItems: "center", gap: "0.5rem" }}>
            <Text fontSize="md">Collected:</Text>
            <Skeleton isLoaded={!loading}>
              <Text fontSize="2xl" fontWeight={"bold"}>
                ₹{amount && (Number(amount) * 0.15).toFixed(2)}
              </Text>
            </Skeleton>
          </Flex> */}
        </Stat>
      </CardBody>
    </Card>
  );
};

export const ParcelStatCard = ({ title, parcels, amount, loading }: any) => {
  return (
    <Card shadow="md" borderRadius="xl">
      <CardBody>
        <Stat>
          <StatLabel fontSize="md" fontWeight="bold">
            {title}
          </StatLabel>

          <Skeleton isLoaded={!loading}>
            <StatNumber fontSize="2xl">{parcels ?? 0} Parcels</StatNumber>
          </Skeleton>

          <Skeleton isLoaded={!loading}>
            <StatHelpText fontSize="xl">
              ₹{amount?.toFixed(2) ?? "0.00"}
            </StatHelpText>
          </Skeleton>
        </Stat>
      </CardBody>
    </Card>
  );
};
